import React, { useState } from 'react';
import {
  Box,
  Typography,
  Collapse,
  IconButton,
  Card,
  CardContent,
  Grid,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  MenuItem,
  Select,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Edit as EditIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { BRAND_COLORS, alpha } from '../../../styles/theme';
import { useDashboardStore } from '../../../stores/dashboardStore';
import { useTranslation } from '../../../i18n';
import type { TranslationKey } from '../../../i18n/useTranslation';
import { useUpdateDroneMetadataMutation } from './dashboard.mutations';

interface TechnicalSpecificationsProps {
  droneSerial?: string;
  metadata?: Record<string, unknown>;
  onDataRefresh?: () => void;
  onMetadataUpdate?: (metadata: Record<string, unknown>) => void;
}

interface SubSection {
  titleKey: string;
  key: string;
}

const subSections: SubSection[] = [
  { titleKey: 'technicalSpecs.configuration', key: 'configuration' },
  { titleKey: 'technicalSpecs.software', key: 'software' },
  { titleKey: 'technicalSpecs.serialNumbers', key: 'serialNumbers' },
  { titleKey: 'technicalSpecs.options', key: 'options' },
];

interface ConfigurationItem {
  display: string;
  key: string;
  /** Translated label; `display` is the fallback and the English name. */
  labelKey?: TranslationKey;
  /**
   * `yesNo` stores a JSON boolean in drones.metadata[key] (#1710). Absent
   * (or null) means nobody has answered yet and shows "Not set", never "No",
   * so the fleet's missing answers stay visible. Default is free text.
   */
  type?: 'text' | 'yesNo';
}

/** The stored answer of a yes/no item: true, false, or null when not set. */
function readYesNo(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

const configurationItems: ConfigurationItem[] = [
  { display: 'Muffler', key: 'muffler' },
  { display: 'FAN', key: 'fan' },
  { display: 'PMU', key: 'pmu' },
  { display: 'ENG HUB', key: 'eng_hub' },
  { display: 'CHT2 T', key: 'cht2_t' },
  { display: 'GEN T', key: 'gen_t' },
  { display: 'FCS orient', key: 'fcs_orient' },
  { display: 'GNSS', key: 'gnss' },
  { display: 'FUEL TANKS', key: 'fuel_tanks' },
  { display: 'Fuel System', key: 'fuel_system' },
  { display: 'UC version', key: 'uc_version' },
  { display: 'PDU version', key: 'pdu_version' },
  { display: 'Air Unit', key: 'air_unit' },
];

const softwareItems: ConfigurationItem[] = [
  { display: 'ECU SW', key: 'ecu_sw' },
  // Two rows, because two different systems answer "what firmware is on this
  // drone?" and they are not interchangeable (#1217).
  //
  // `UC_FW` is what the UC Flasher RECORDED INSTALLING — a git commit, written
  // by public.update_drone_uc_fw and joined to public.flash_events. It is the
  // EASA traceability record.
  //
  // `UC_FW_REPORTED` is what the airframe's own telemetry SAID on its most
  // recent flight, maintained by public.update_drone_metadata_from_flights.
  //
  // They used to be one key, and the flights trigger overwrote the flasher's
  // commit on every upload. Splitting them means a drone with no flash event
  // now shows a blank "UC SW" — which is the truth, and the reported row is
  // what keeps the telemetry value on screen instead of looking like data loss.
  // A disagreement between these two rows is a real signal: the installed build
  // is not the one flying.
  { display: 'UC SW', key: 'UC_FW' },
  { display: 'UC SW (reported)', key: 'UC_FW_REPORTED' },
  { display: 'FCS SW', key: 'fcs_sw' },
  { display: 'FCS config', key: 'fcs_config' },
  { display: 'QGC SW', key: 'qgc_sw' },
];

const serialNumberItems: ConfigurationItem[] = [
  { display: 'Thrtl SN', key: 'thrtl_sn' },
  { display: 'Eng SN', key: 'eng_sn' },
  { display: 'Pump1 SN', key: 'pump1_sn' },
  { display: 'Pump 2 SN', key: 'pump2_sn' },
  { display: 'IGN. SN', key: 'ign_sn' },
  { display: 'ECU SN', key: 'ecu_sn' },
  { display: 'GEN  Rotor SN', key: 'gen_rotor_sn' },
  { display: 'GEN Stator SN', key: 'gen_stator_sn' },
];

const optionItems: ConfigurationItem[] = [
  { display: 'LiDAR', key: 'lidar' },
  { display: 'FPV1', key: 'fpv1' },
  { display: 'FPV2', key: 'fpv2' },
  { display: 'Sec. Link', key: 'sec_link' },
  { display: 'ADS-B', key: 'ads_b' },
  {
    display: "Temp sensor cable's fixing clip",
    key: 'temp_sensor_cable_clip',
    labelKey: 'technicalSpecs.tempSensorCableClip',
    type: 'yesNo',
  },
];

export const TechnicalSpecifications: React.FC<TechnicalSpecificationsProps> = ({
  droneSerial,
  metadata = {},
  onDataRefresh,
  onMetadataUpdate,
}) => {
  const { t } = useTranslation();
  const [mainExpanded, setMainExpanded] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    configuration: false,
    software: false,
    serialNumbers: false,
    options: false,
  });

  // State for editing configuration fields
  const [editingField, setEditingField] = useState<string | null>(null);
  const [tempValue, setTempValue] = useState<string>('');
  const [lastSavedField, setLastSavedField] = useState<string | null>(null);

  const updateMetadataMutation = useUpdateDroneMetadataMutation(droneSerial ?? '');
  const isLoading = updateMetadataMutation.isPending;

  const labelOf = (item: ConfigurationItem) => (item.labelKey ? t(item.labelKey) : item.display);

  const toggleSubSection = (key: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleEditClick = (item: ConfigurationItem) => {
    setEditingField(item.key);
    const value = metadata[item.key];
    if (item.type === 'yesNo') {
      const answer = readYesNo(value);
      setTempValue(answer === null ? '' : answer ? 'yes' : 'no');
      return;
    }
    setTempValue(value !== null && value !== undefined ? String(value) : '');
  };

  const handleCancelEdit = () => {
    setEditingField(null);
    setTempValue('');
  };

  const handleSave = (item: ConfigurationItem) => {
    if (!droneSerial) return;
    const itemKey = item.key;

    let value: string | boolean = tempValue;
    if (item.type === 'yesNo') {
      // Nothing picked yet: there is no answer to save, and "not set" stays.
      if (tempValue !== 'yes' && tempValue !== 'no') return;
      value = tempValue === 'yes';
    }

    // Optimistic update - update UI immediately
    const optimisticMetadata = {
      ...metadata,
      [itemKey]: value,
    };
    onMetadataUpdate?.(optimisticMetadata);

    updateMetadataMutation.mutate(
      { [itemKey]: value },
      {
        onSuccess: (response) => {
          // Update with server response (in case server modified the data)
          if (response.metadata) {
            onMetadataUpdate?.(response.metadata);
          }

          // Refresh overview data so next full refresh gets fresh data
          useDashboardStore.getState().refreshOverviewData();

          setLastSavedField(labelOf(item));
          setEditingField(null);
          setTempValue('');
        },
        onError: () => {
          console.error('Error updating metadata');
          // Revert optimistic update on error by refreshing data
          onDataRefresh?.();
        },
      }
    );
  };

  const handleCloseSuccess = () => {
    updateMetadataMutation.reset();
    setLastSavedField(null);
  };

  const displayValue = (item: ConfigurationItem): string => {
    const value = metadata[item.key];
    if (item.type === 'yesNo') {
      const answer = readYesNo(value);
      if (answer === null) return t('technicalSpecs.notSet');
      return answer ? t('common.yes') : t('common.no');
    }
    return value !== null && value !== undefined ? String(value) : '-';
  };

  const renderItemGrid = (items: ConfigurationItem[]) => (
    <Grid container spacing={2}>
      {items.map((item) => (
        <Grid item xs={12} sm={6} md={4} key={item.key}>
          <Box>
            <Typography
              variant="body2"
              sx={{
                color: BRAND_COLORS.forestGreen.main,
                fontWeight: 600,
                mb: 0.5,
              }}
            >
              {labelOf(item)}
            </Typography>
            {editingField === item.key ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {item.type === 'yesNo' ? (
                  <Select
                    size="small"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    disabled={isLoading}
                    displayEmpty
                    fullWidth
                    inputProps={{ 'aria-label': labelOf(item) }}
                    renderValue={(selected) =>
                      selected === 'yes'
                        ? t('common.yes')
                        : selected === 'no'
                          ? t('common.no')
                          : t('technicalSpecs.notSet')
                    }
                    sx={{ '& .MuiSelect-select': { fontSize: '0.875rem', py: 0.5 } }}
                  >
                    <MenuItem value="yes">{t('common.yes')}</MenuItem>
                    <MenuItem value="no">{t('common.no')}</MenuItem>
                  </Select>
                ) : (
                  <TextField
                    size="small"
                    value={tempValue}
                    onChange={(e) => setTempValue(e.target.value)}
                    disabled={isLoading}
                    fullWidth
                    sx={{
                      '& .MuiInputBase-input': {
                        fontSize: '0.875rem',
                        py: 0.5,
                      },
                    }}
                  />
                )}
                <IconButton
                  size="small"
                  aria-label={t('common.save')}
                  onClick={() => handleSave(item)}
                  disabled={isLoading || (item.type === 'yesNo' && tempValue === '')}
                  sx={{ color: '#4caf50', p: 0.5 }}
                >
                  {isLoading ? <CircularProgress size={16} /> : <CheckIcon sx={{ fontSize: 16 }} />}
                </IconButton>
                <IconButton
                  size="small"
                  aria-label={t('common.cancel')}
                  onClick={handleCancelEdit}
                  disabled={isLoading}
                  sx={{ color: '#f44336', p: 0.5 }}
                >
                  <CloseIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ flex: 1 }}
                  data-testid={`tech-spec-value-${item.key}`}
                >
                  {displayValue(item)}
                </Typography>
                {droneSerial && (
                  <IconButton
                    size="small"
                    aria-label={`${t('common.edit')} ${labelOf(item)}`}
                    onClick={() => handleEditClick(item)}
                    sx={{
                      color: BRAND_COLORS.forestGreen.main,
                      p: 0.5,
                      '&:hover': {
                        backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.1),
                      },
                    }}
                  >
                    <EditIcon sx={{ fontSize: 14 }} />
                  </IconButton>
                )}
              </Box>
            )}
          </Box>
        </Grid>
      ))}
    </Grid>
  );

  const sectionItemsMap: Record<string, ConfigurationItem[]> = {
    configuration: configurationItems,
    software: softwareItems,
    serialNumbers: serialNumberItems,
    options: optionItems,
  };

  return (
    <Box>
      {/* Main Header - Always Visible */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={() => setMainExpanded(!mainExpanded)} size="small" sx={{ p: 0.5 }}>
            {mainExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Box>
            <Typography
              variant="h5"
              sx={{
                cursor: 'pointer',
                color: BRAND_COLORS.forestGreen.main,
              }}
              onClick={() => setMainExpanded(!mainExpanded)}
            >
              {t('technicalSpecs.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('technicalSpecs.subtitle')}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Collapsible Content */}
      <Collapse in={mainExpanded}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {subSections.map((section) => (
            <Card
              key={section.key}
              sx={{
                backgroundColor: alpha(BRAND_COLORS.forestGreen.main, 0.05),
                border: `1px solid ${alpha(BRAND_COLORS.forestGreen.main, 0.2)}`,
                borderRadius: '8px',
                boxShadow: 'none',
                transition: 'all 0.2s ease',
              }}
            >
              <Box
                onClick={() => toggleSubSection(section.key)}
                sx={{
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    color: BRAND_COLORS.forestGreen.main,
                    fontWeight: 600,
                  }}
                >
                  {t(section.titleKey as TranslationKey)}
                </Typography>
                <IconButton size="small" sx={{ p: 0 }}>
                  {expandedSections[section.key] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </IconButton>
              </Box>

              <Collapse in={expandedSections[section.key]}>
                <CardContent sx={{ pt: 0 }}>
                  {sectionItemsMap[section.key] ? (
                    renderItemGrid(sectionItemsMap[section.key])
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {t(section.titleKey as TranslationKey)}
                    </Typography>
                  )}
                </CardContent>
              </Collapse>
            </Card>
          ))}
        </Box>
      </Collapse>

      {/* Success notification */}
      <Snackbar
        open={updateMetadataMutation.isSuccess}
        autoHideDuration={3000}
        onClose={handleCloseSuccess}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSuccess} severity="success">
          {lastSavedField
            ? t('technicalSpecs.updatedSuccessfully', { field: lastSavedField })
            : t('technicalSpecs.updatedSuccessfully', { field: '' })}
        </Alert>
      </Snackbar>
    </Box>
  );
};
